import { Image as ImageIcon, Upload as UploadIcon } from 'lucide-react';
import { ChangeEvent, DragEvent, Fragment, useEffect, useRef, useState } from 'react';
import { Button, Header, Image, Segment } from 'semantic-ui-react';
import './styles.css';
import Alert from '../../utils/alert';

const maxFileSize = 50 * 1024; // 50KB in bytes
const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png'];

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) {
        return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const convertedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
    return `${convertedSize} ${sizes[i]}`;
};

const validateFile = (file: File): boolean => {
    if (!acceptedTypes.includes(file.type)) {
        Alert.warn(`Please select a valid image file. Accepted types: ${acceptedTypes.join(', ')}`);
        return false;
    }

    if (file.size > maxFileSize) {
        Alert.warn(`File size must be less than ${formatFileSize(maxFileSize)}`);
        return false;
    }

    return true;
};

type ImageUploadProps = {
    onUpload: (file: File) => void;
};

export default function ImageUpload({ onUpload }: ImageUploadProps) {
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [isDragOver, setIsDragOver] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        return () => {
            if (preview) {
                URL.revokeObjectURL(preview);
            }
        };
    }, [preview]);

    const handleFileSelect = (file: File): void => {
        if (file && validateFile(file)) {
            if (preview) {
                URL.revokeObjectURL(preview);
            }

            setSelectedImage(file);
            const previewUrl = URL.createObjectURL(file);
            setPreview(previewUrl);
        }
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const triggerFileSelect = (): void => {
        fileInputRef.current?.click();
    };

    const clearImage = (): void => {
        if (preview) {
            URL.revokeObjectURL(preview);
        }
        setSelectedImage(null);
        setPreview(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const uploadImage = async (): Promise<void> => {
        if (!selectedImage) {
            return;
        }
        setIsUploading(true);
        onUpload(selectedImage);
    };

    const getDropzoneClass = (): string => {
        return `image-upload-dropzone ${isDragOver ? 'image-upload-dropzone--active' : ''}`;
    };

    return (
        <Segment className="image-upload-container">
            <Header as="h2" textAlign="center">
                Upload Image
            </Header>

            <input
                ref={fileInputRef}
                type="file"
                accept={acceptedTypes.join(',')}
                onChange={handleInputChange}
                className="image-upload-hidden-input"
            />

            {!preview ? (
                <Segment
                    textAlign="center"
                    className={getDropzoneClass()}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={triggerFileSelect}
                >
                    <UploadIcon size={48} color="#9ca3af" strokeWidth={1} />
                    <p className="image-upload-dropzone__text">Click to select or drag and drop an image</p>
                    <p className="image-upload-dropzone__subtext">PNG, JPG up to {formatFileSize(maxFileSize)}</p>
                </Segment>
            ) : (
                <Fragment>
                    <div className="image-upload-image-container">
                        <Image src={preview} alt="Preview" size="medium" />
                        <Button
                            onClick={clearImage}
                            disabled={isUploading}
                            className="image-upload-close-button"
                            icon="close"
                            circular
                            color="red"
                            size="mini"
                        />
                    </div>

                    <div className="image-upload-file-info">
                        <div className="image-upload-file-name-container">
                            <ImageIcon className="image-upload-file-icon" />
                            <span className="image-upload-file-name">{selectedImage?.name}</span>
                        </div>
                        <div className="image-upload-file-details">
                            <span>{formatFileSize(selectedImage?.size || 0)}</span>
                            <span>{selectedImage?.type}</span>
                        </div>
                    </div>
                    <div className="flex flex-column gap-10">
                        <Button fluid onClick={triggerFileSelect} disabled={isUploading}>
                            Choose Different Image
                        </Button>
                        <Button fluid positive onClick={uploadImage} disabled={isUploading} loading={isUploading}>
                            Upload Image
                        </Button>
                    </div>
                </Fragment>
            )}
        </Segment>
    );
}
